import { FC, Fragment, SyntheticEvent } from 'react';
import ProposalHtmlTripadvisor from './ProposalHtlTripadvisor';
import ProposalHtmlGoogle from './ProposalHtlGoogle';
import { ProposalHotel } from 'ts/shared/interfaces/ProposalData';
import { checkAHAVI, formatPriceCurrency, getCurrency } from 'utils/utils';
import { useTranslation } from 'react-i18next';
import ProposalEditHead from './ProposalEditHead';
import useRemovedItem from './useRemovedItem';
import withErrorBoundary from '../../withErrorBoundary';
import { dateFormat } from 'utils/shared/utils';
import { parseClaudeHotelInfoConfigs, resolveClaudeHotelInfoConfig } from 'utils/shared/claudeProposalInformation';
import { getAdjustedProposalCancellationDate, isValidProposalCancellationDate } from 'utils/shared/proposal';
import { parseHotelInfoConfigs, resolveHotelInfoConfig } from 'utils/shared/hotelProposalInformation';
import ProposalHtmlHotelInformation from './ProposalHtlHotelInformation';
import ProposalHtlClaudeHotelInformation from './ProposalHtlClaudeHotelInformation';
import { imgBg } from 'constants/generic';

interface ProposalHtlProps {
  hotels: ProposalHotel[];
  hideWrapper?: boolean;
  displayPrice?: boolean;
  proposalId: string;
  isPackage?: boolean;
  showCancellationDate?: boolean;
  cancellationDateDelta?: number;
  externalParam_1?: string;
}

const ProposalHtl: FC<ProposalHtlProps> = ({
  hotels,
  hideWrapper,
  showCancellationDate = false,
  cancellationDateDelta = 0,
  externalParam_1,
  ...props
}) => {
  const { t } = useTranslation();
  const removedItem = useRemovedItem(props.proposalId);
  const claudeHotelInfoConfigs = parseClaudeHotelInfoConfigs(externalParam_1);
  const hotelInfoConfigs = parseHotelInfoConfigs(externalParam_1);

  const onImageLoadError = ({ currentTarget }: SyntheticEvent<HTMLImageElement, Event>) => {
    currentTarget.onerror = null;
    currentTarget.src = imgBg;
  };

  const getHotelRooms = (hotel: ProposalHotel) => {
    const groupedByAmenity: { [key: string]: typeof hotel.rooms } = {};
    const EMPTY_AMENITY = '';

    const addRoomToGroup = (code: string, room: (typeof hotel.rooms)[number]) => {
      if (!groupedByAmenity[code]) {
        groupedByAmenity[code] = [];
      }
      if (!groupedByAmenity[code].includes(room)) {
        groupedByAmenity[code].push(room);
      }
    };

    hotel.rooms.forEach((room) => {
      if (!Array.isArray(room.amenities) || room.amenities.length === 0) {
        addRoomToGroup(EMPTY_AMENITY, room);
        return;
      }
      room.amenities.forEach((amenity: any) => {
        const code = amenity?.code ?? EMPTY_AMENITY;
        addRoomToGroup(code, room);
      });
    });

    const result = Object.entries(groupedByAmenity).map(([code, rooms]) => ({
      code,
      rooms,
    }));

    return result;
  };

  const renderCancellationDate = (cxl: string) => {
    if (!showCancellationDate || !isValidProposalCancellationDate(cxl)) {
      return null;
    }

    const cxlDate = getAdjustedProposalCancellationDate(cxl, cancellationDateDelta);

    return (
      <div className="mt-5">
        {cxlDate < new Date() && (
          <div className="pr-color-danger pr-text-bold">{t('hotel.full_cancellation_fee')}</div>
        )}
        {cxlDate > new Date() && (
          <div className="pr-color-success pr-text-bold">
            {t('hotel.free_cancel_before')} {dateFormat(cxlDate, 'dd MMM yyyy')}
          </div>
        )}
      </div>
    );
  };

  return (
    <Fragment>
      {hotels.map((hotel, index) => (
        <div className="pr-hotel-card-wr" key={index}>
          <div
            className={
              (hideWrapper ? '' : 'pr-hotel-card') + (removedItem ? ' pr-hotel-removed' : '')
            }
            key={index}
          >
            {!hideWrapper && (
              <div className="pr-hotel-card-title">
                <div className="pr-hotel-card-title-main">
                  <div className="pr-hotel-card-title-name">
                    {t('hotel.hotel')}: {hotel.hotel.name}
                  </div>
                  <div className="pr-hotel-card-title-meta">
                    <span>
                      {dateFormat(hotel.rooms[0].startDate, 'dd MMM yyyy', 'en')}
                    </span>
                    <span>({hotel.rooms[0].nights})</span>
                  </div>
                </div>
                <ProposalEditHead
                  proposalId={props.proposalId}
                  displayPrice={!!props.displayPrice}
                />
              </div>
            )}
            {hideWrapper && !props.isPackage && (
              <div className="pr-hotel-title pr-flex-row">
                {t('hotel.hotel')}:{' '}
                <div className="pr-flex-row pr-ph-5">
                  <span>{hotel.hotel.name}</span>,
                  <span className="pr-ph-5">
                    {dateFormat(hotel.rooms[0].startDate, 'dd MMM yyyy', 'en')}
                  </span>
                  <span>({hotel.rooms[0].nights})</span>
                </div>
              </div>
            )}

            <div className="pr-hotel-content pr-break-avoid">
              {!!hotel.listOfImages?.[0]?.code && (
                <div className="pr-hotel-image-container">
                  <img
                    className="pr-hotel-image"
                    src={hotel.listOfImages?.[0]?.code}
                    alt={'1'}
                    onError={onImageLoadError}
                  />
                </div>
              )}

              <div className="pr-hotel-details">
                <div className="pr-hotel-flex-row">
                  <span className="pr-hotel-label">{t('search.destination')}:</span>
                  {!!hotel.city.name && (
                    <span className="pr-hotel-value">
                      {hotel.city.name},{hotel.country.name}
                    </span>
                  )}
                  {!!hotel.hotel.name && !hotel.city.name && (
                    <span className="pr-hotel-value">{hotel.hotel.name}</span>
                  )}
                </div>
                {!!hotel.star && (
                  <div className="pr-hotel-detail-row">
                    <span className="pr-hotel-label">{t('hotel.stars')}:</span>
                    <span className="pr-hotel-value">{hotel.star}</span>
                  </div>
                )}

                {!!hotel.adress && (
                  <div className="pr-hotel-detail-row">
                    <span className="pr-hotel-label">{t('hotel.address')}:</span>
                    <span className="pr-hotel-value">{hotel.adress}</span>
                  </div>
                )}

                <div className="pr-hotel-flex-row">
                  <span className="pr-hotel-label">{t('search.check_in')}:</span>
                  <span className="pr-hotel-value">
                    {dateFormat(hotel.rooms[0].startDate, 'dd MMM yyyy', 'en')}

                    <span> ({hotel.rooms[0].nights})</span>
                  </span>
                </div>
                <div className="pr-hotel-flex-row">
                  <span className="pr-hotel-label">{t('search.check_out')}:</span>
                  <span className="pr-hotel-value">
                    {dateFormat(hotel.rooms[0].endtDate, 'dd MMM yyyy', 'en')}
                  </span>
                </div>

                {getHotelRooms(hotel).length > 1 && (
                  <div className="pr-hotel-flex-row">
                    <span className="pr-hotel-label"></span>
                    <span className="">{t('proposal.room_price_option')}:</span>
                  </div>
                )}

                {getHotelRooms(hotel).map((hr, i) => (
                  <div key={i} className="pr-hotel-flex-row">
                    <span className="pr-hotel-label">
                      {t('hotel.room')}
                      {hotel.rooms.length > 1 && <span> {i + 1}#</span>}:
                    </span>
                    <div>
                      <div className="pr-hotel-value pr-text-lh">
                        {hr.rooms.map((room, rIndex) => (
                          <div key={room.room.code}>
                            {room.room.name?.replace(room.board.name, '')}{' '}
                            {!!room.board.name && <span>({room.board.name})</span>}

                            {rIndex === 0 && !!hr.rooms[0].supplements?.[0]?.code && !!props.displayPrice && (
                              <span>
                                {' '}
                                -{' '}
                                {
                                  formatPriceCurrency(
                                    +hr.rooms[0].supplements[0].code,
                                    getCurrency(hr.rooms[0].supplements[0].name)
                                  )}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>

                      {renderCancellationDate(hr.rooms[0].cxl)}
                    </div>
                  </div>
                ))}
                <div></div>
              </div>
            </div>

            {!!hotel.displayNote?.name && !props.isPackage && (
              <div
                className={
                  'pr-notes' + (checkAHAVI(hotel.displayNote?.name) ? ' pr-rtl' : ' pr-ltr')
                }
              >
                <div
                  className="pr-bold"
                  dangerouslySetInnerHTML={{
                    __html: hotel.displayNote?.name.replaceAll(/\n/g, '<br />'),
                  }}
                ></div>
              </div>
            )}
            <ProposalHtmlHotelInformation
              hotelInformation={hotel.hotelInformatiom}
              config={resolveHotelInfoConfig(hotelInfoConfigs, hotel, index)}
            />
            <ProposalHtlClaudeHotelInformation
              aI_HotelInformation={hotel.aI_HotelInformatiom}
              config={resolveClaudeHotelInfoConfig(claudeHotelInfoConfigs, hotel, index)}
            />
            <ProposalHtmlGoogle serpHotel={hotel.serpHotel} />
            <ProposalHtmlTripadvisor tripAdvisorHotel={hotel.tripAdvisorHotel} />
          </div>
          {!hideWrapper && <div className="pr-divider"></div>}
        </div>
      ))}
    </Fragment>
  );
};

export default withErrorBoundary(ProposalHtl, 'ProposalHtl');
